import React, { useEffect, useState, useCallback } from 'react';
import { Flowbite } from 'flowbite-react';
import { Button, Label } from 'flowbite-react';
import { name, serverURL, websiteURL, logo, company } from '../constants';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { AiOutlineLoading } from 'react-icons/ai';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import { resolveHomePath, getSessionAuth, persistLoginSession, currentBrowserHost } from '../shared/auth/session';
import AuthOrgBrand, { useOrgAuthUi } from '../shared/branding/AuthOrgBrand';
import OrgJoinRoleToggle, { WorkspaceCta } from '../shared/auth/AuthPathChooser';
import AuthShell, { AuthCard } from '../shared/auth/AuthShell';
import { trackSignUpConversion } from '../utils/cookieConsent';

const SignUp = () => {
    const { t } = useTranslation();
    const authUi = useOrgAuthUi();
    const [mName, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [nameError, setNameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    /** Platform: always learner. Tenant: learner | instructor membership role. */
    const [accountType, setAccountType] = useState('student');
    const [inviteCode, setInviteCode] = useState('');
    const [invitePreview, setInvitePreview] = useState(null);
    const [invitePreviewLoading, setInvitePreviewLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [marketingEmailConsent, setMarketingEmailConsent] = useState(false);
    const [termsError, setTermsError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const canSelfRegister = authUi.allowSelfSignup || Boolean(inviteCode.trim());
    const orgStartPath = `/organizations/start${searchParams.get('plan') ? `?plan=${encodeURIComponent(searchParams.get('plan'))}` : ''}`;

    const redirectSignIn = useCallback(() => {
        const invite = inviteCode.trim() || searchParams.get('invite') || '';
        navigate(invite ? `/signin?invite=${encodeURIComponent(invite)}` : '/signin');
    }, [navigate, inviteCode, searchParams]);

    const redirectHome = useCallback(() => {
        if (location.state && location.state.courseIdea && location.state.redirectToCreate) {
            navigate("/create-choice", { state: { courseIdea: location.state.courseIdea } });
        } else if (location.state && location.state.courseIdeaData && location.state.redirectToCourseIdeas) {
            navigate("/course-ideas", { state: { prefillAnswers: location.state.courseIdeaData } });
        } else {
            window.location.href = resolveHomePath(getSessionAuth());
        }
    }, [navigate, location.state]);

    useEffect(() => {
        if (getSessionAuth().auth) {
            redirectHome();
        }

        const inviteFromUrl = searchParams.get('invite');
        if (inviteFromUrl) {
            setInviteCode(inviteFromUrl.trim().toUpperCase());
            setAccountType('student');
        }
        const intent = searchParams.get('intent');
        // Teaching = organization workspace (not a personal instructor product)
        if (intent === 'teach' || intent === 'instructor' || intent === 'org' || intent === 'organization') {
            window.location.replace(orgStartPath);
            return;
        }
        if (intent === 'learn' || intent === 'student') setAccountType('student');
        // Platform personal accounts are learners; instructor only on tenant join
        if (!authUi.isTenantHost) setAccountType('student');
    }, [redirectHome, searchParams, orgStartPath, authUi.isTenantHost]);

    const previewInvite = async () => {
        const code = inviteCode.trim().toUpperCase();
        if (!code) {
            setInvitePreview(null);
            return;
        }
        setInvitePreviewLoading(true);
        try {
            const res = await axios.get(`${serverURL}/api/invites/${encodeURIComponent(code)}/preview`);
            setInvitePreview(res.data);
        } catch {
            setInvitePreview({ valid: false, message: t('signup.invite.invalid', 'Invalid invite code') });
        } finally {
            setInvitePreviewLoading(false);
        }
    };

    const showToast = async (msg, type = 'error') => {
        setProcessing(false);
        toast(msg, {
            type: type,
            position: "top-center",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            style: {
                backgroundColor: type === 'error' ? '#ef4444' : '#10b981',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500'
            }
        });
    }

    const clearErrors = () => {
        setError('');
        setNameError('');
        setEmailError('');
        setPasswordError('');
        setTermsError('');
    }

    const handleSignup = async (e) => {
        e.preventDefault();
        clearErrors();
        
        // Validation
        let hasError = false;
        if (!mName) {
            setNameError(t('signup.validation.nameRequired', 'Name is required'));
            hasError = true;
        }
        
        if (!email) {
            setEmailError(t('signup.validation.emailRequired', 'Email is required'));
            hasError = true;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError(t('signup.validation.emailInvalid', 'Please enter a valid email address'));
            hasError = true;
        }
        
        if (!password) {
            setPasswordError(t('signup.validation.passwordRequired', 'Password is required'));
            hasError = true;
        } else if (password.length < 9) {
            setPasswordError(t('signup.validation.passwordLength', 'Password must be at least 9 characters'));
            hasError = true;
        }

        if (!acceptedTerms) {
            setTermsError(t('signup.validation.termsRequired', 'Please accept the Terms and Privacy Policy'));
            hasError = true;
        }
        
        if (hasError) {
            setError(t('signup.validation.requiredFields', 'Please fill in all required fields correctly'));
            showToast(t('signup.validation.requiredFields', 'Please fill in all required fields correctly'), 'error');
            return;
        }
        
        const postURL = serverURL + '/api/signup';
        try {
            setProcessing(true);
            const response = await axios.post(postURL, {
                email,
                mName,
                password,
                inviteCode: inviteCode.trim() || undefined,
                accountType: authUi.isTenantHost
                    ? (inviteCode.trim() ? 'student' : accountType)
                    : 'student',
                host: currentBrowserHost() || undefined,
                termsAccepted: true,
                marketingEmailConsent: Boolean(marketingEmailConsent),
            }, {
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (response.data.success) {
                clearErrors();
                showToast(response.data.message || t('signup.success', 'Account created successfully!'), 'success');
                try { trackSignUpConversion(); } catch { /* ignore */ }
                if (!response.data.accessToken && !response.data.token) {
                    const msg = t('signup.errors.missingToken', 'Account created but session token missing. Please sign in.');
                    setError(msg);
                    showToast(msg, 'error');
                    redirectSignIn();
                    return;
                }
                persistLoginSession(
                    response.data.userData || {
                        _id: response.data.userId,
                        email,
                        mName,
                        type: 'free',
                        role: response.data.role || 'student',
                    },
                    response.data.uploadLimits,
                    {
                        accessToken: response.data.accessToken || response.data.token,
                        refreshToken: response.data.refreshToken,
                        organizationId: response.data.organizationId,
                        orgRole: response.data.orgRole,
                        orgSlug: response.data.orgSlug || authUi.slug,
                        platformAdmin: response.data.platformAdmin,
                        role: response.data.role,
                        uid: response.data.userId,
                    }
                );
                if (response.data.inviteRedemption?.success) {
                    showToast(t('signup.invite.redeemed', 'Class invite applied successfully!'), 'success');
                }
                sendEmail(email, mName);
            } else {
                const errorMsg = response.data.message || t('signup.errors.createFailed', 'Failed to create account. Please try again.');
                setError(errorMsg);
                showToast(errorMsg, 'error');
            }
        } catch (error) {
            logger.error('Signup error:', error);
            let errorMsg = '';
            if (error.response?.data?.code === 'SELF_SIGNUP_DISABLED') {
                errorMsg = error.response.data.message;
            } else if (error.code === 'ECONNABORTED') {
                errorMsg = t('signup.errors.timeout', 'Request timed out. Please try again.');
            } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                errorMsg = t('signup.errors.networkError', 'Cannot reach the server. Make sure the API is running (npm run dev) at {{serverURL}}.', { serverURL });
            } else if (!error.response) {
                errorMsg = t('signup.errors.serverUnreachable', 'Unable to reach the server. Please ensure the backend is running on {{serverURL}}.', { serverURL });
            } else if (error.response?.status === 503) {
                errorMsg = t('signup.errors.serviceUnavailable', 'Service temporarily unavailable. Please try again later.');
            } else if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
            } else if (error.response?.status === 409) {
                errorMsg = t('signup.errors.emailExists', 'An account with this email already exists.');
                setEmailError(errorMsg);
            } else {
                errorMsg = t('signup.errors.serverError', 'An error occurred. Please try again.');
            }
            setError(errorMsg);
            showToast(errorMsg, 'error');
        } finally {
            setProcessing(false);
        }
    };

    async function sendEmail(mEmail, mName) {

        try {
            const dataToSend = {
                subject: t('signup.welcomeEmailSubject', { name }),
                to: mEmail,
                html: `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
                <meta http-equiv="Content-Type" content="text/html charset=UTF-8" />
                <html lang="en">
                
                  <head></head>
                 <div id="__react-email-preview" style="display:none;overflow:hidden;line-height:1px;opacity:0;max-height:0;max-width:0">${t('signup.welcomeEmailPreview', { name })}<div> ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏﻿ ‌​‍‎‏ ‌​‍‎‏﻿ ‌​‍‎‏﻿</div>
                 </div>
                
                  <body style="margin-left:auto;margin-right:auto;margin-top:auto;margin-bottom:auto;background-color:rgb(255,255,255);font-family:ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, &quot;Helvetica Neue&quot;, Arial, &quot;Noto Sans&quot;, sans-serif, &quot;Apple Color Emoji&quot;, &quot;Segoe UI Emoji&quot;, &quot;Segoe UI Symbol&quot;, &quot;Noto Color Emoji&quot;">
                    <table align="center" role="presentation" cellSpacing="0" cellPadding="0" border="0" width="100%" style="max-width:37.5em;margin-left:auto;margin-right:auto;margin-top:40px;margin-bottom:40px;width:465px;border-radius:0.25rem;border-width:1px;border-style:solid;border-color:rgb(234,234,234);padding:20px">
                      <tr style="width:100%">
                        <td>
                          <table align="center" border="0" cellPadding="0" cellSpacing="0" role="presentation" width="100%" style="margin-top:32px">
                            <tbody>
                              <tr>
                                <td><img alt="Vercel" src="${logo}" width="40" height="37" style="display:block;outline:none;border:none;text-decoration:none;margin-left:auto;margin-right:auto;margin-top:0px;margin-bottom:0px" /></td>
                              </tr>
                            </tbody>
                          </table>
                          <h1 style="margin-left:0px;margin-right:0px;margin-top:30px;margin-bottom:30px;padding:0px;text-align:center;font-size:24px;font-weight:400;color:rgb(0,0,0)">${t('signup.welcomeEmailHeading')} <strong>${name}</strong></h1>
                          <p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0)">Hello <strong>${mName}</strong>,</p>
                          <p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0)">${t('signup.welcomeEmailBody', { name })}</p>
                          <table align="center" border="0" cellPadding="0" cellSpacing="0" role="presentation" width="100%" style="margin-bottom:32px;margin-top:32px;text-align:center">
                            <tbody>
                              <tr>
                                <td><a href="${websiteURL}" target="_blank" style="p-x:20px;p-y:12px;line-height:100%;text-decoration:none;display:inline-block;max-width:100%;padding:12px 20px;border-radius:0.25rem;background-color:rgb(0,0,0);text-align:center;font-size:12px;font-weight:600;color:rgb(255,255,255);text-decoration-line:none"><span></span><span style="p-x:20px;p-y:12px;max-width:100%;display:inline-block;line-height:120%;text-decoration:none;text-transform:none;mso-padding-alt:0px;mso-text-raise:9px"><span>${t('signup.welcomeEmailCta')}</span></a></td>
                              </tr>
                            </tbody>
                          </table>
                          <p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0)">Best,<p target="_blank" style="color:rgb(0,0,0);text-decoration:none;text-decoration-line:none">The <strong>${company}</strong> Team</p></p>
                          </td>
                      </tr>
                    </table>
                  </body>
                
                </html>`
            };
            const postURL = serverURL + '/api/data';
            await axios.post(postURL, dataToSend).then(res => {
                redirectHome();
            }).catch(error => {
                redirectHome();
            });

        } catch (error) {
            redirectHome();
        }

    }

    return (
        <Flowbite>
            <AuthShell quoteKey="signup" showMarketing={!authUi.isTenantHost}>
                <AuthCard>
                    <AuthOrgBrand className="mb-5" />
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                            {authUi.isTenantHost
                                ? t('signup.titleOrg', 'Join {{name}}', { name: authUi.companyName })
                                : t('signup.title', 'Create your account')}
                        </h1>
                        <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
                            {authUi.isTenantHost
                                ? t('signup.subtitleOrg', 'One login for this organization workspace')
                                : t('signup.subtitle', 'Start learning on Coursolo. Schools and companies create a workspace separately.')}
                        </p>
                    </div>

                    {!authUi.allowSelfSignup && !inviteCode.trim() ? (
                        <div className="mb-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 text-sm text-amber-900 dark:text-amber-100">
                            {t('signup.inviteOnly', 'This organization is invite-only. Ask your admin for access, or use SSO.')}
                            <div className="mt-3 space-y-2">
                                <input
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 rounded-xl border border-amber-200 bg-white dark:bg-slate-900 text-sm uppercase tracking-wider"
                                    placeholder={t('signup.invite.placeholder', 'TCH-XXXXXX')}
                                />
                                {authUi.ssoEnabled && (
                                    <a
                                        className="block text-center py-2.5 rounded-xl text-white text-sm font-semibold"
                                        style={{ backgroundColor: authUi.primary }}
                                        href={`${serverURL}/api/auth/sso/start?slug=${encodeURIComponent(authUi.slug || '')}`}
                                    >
                                        {t('signin.sso', 'Continue with SSO')}
                                    </a>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {error ? (
                        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300">
                            {error}
                        </div>
                    ) : null}

                    {canSelfRegister && (
                        <form onSubmit={handleSignup} className="space-y-4">
                            {authUi.isTenantHost ? (
                                <OrgJoinRoleToggle
                                    orgName={authUi.companyName}
                                    value={accountType}
                                    disabledTeacher={Boolean(inviteCode.trim())}
                                    onChange={(next) => {
                                        if (inviteCode.trim() && next !== 'student') return;
                                        setAccountType(next);
                                    }}
                                />
                            ) : (
                                <WorkspaceCta orgStart={orgStartPath} />
                            )}

                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="name1">
                                    {t('signup.name', 'Name')}
                                </Label>
                                <input
                                    id="name1"
                                    value={mName}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        if (nameError) setNameError('');
                                        if (error) clearErrors();
                                    }}
                                    className={`w-full rounded-xl border bg-white dark:bg-slate-950 px-3.5 py-2.5 text-sm ${
                                        nameError ? 'border-red-400' : 'border-slate-200 dark:border-slate-600'
                                    }`}
                                    type="text"
                                    placeholder={t('signup.namePlaceholder', 'Your name')}
                                    autoComplete="name"
                                />
                                {nameError ? <p className="text-xs text-red-600">{nameError}</p> : null}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="email1">
                                    {t('common.email', 'Email')}
                                </Label>
                                <input
                                    id="email1"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (emailError) setEmailError('');
                                        if (error) clearErrors();
                                    }}
                                    className={`w-full rounded-xl border bg-white dark:bg-slate-950 px-3.5 py-2.5 text-sm ${
                                        emailError ? 'border-red-400' : 'border-slate-200 dark:border-slate-600'
                                    }`}
                                    type="email"
                                    placeholder={t('signup.emailPlaceholder', 'you@example.com')}
                                    autoComplete="email"
                                />
                                {emailError ? <p className="text-xs text-red-600">{emailError}</p> : null}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="password1">
                                    {t('common.password', 'Password')}
                                </Label>
                                <div className="relative">
                                    <input
                                        id="password1"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (passwordError) setPasswordError('');
                                            if (error) clearErrors();
                                        }}
                                        className={`w-full rounded-xl border bg-white dark:bg-slate-950 px-3.5 py-2.5 pr-11 text-sm ${
                                            passwordError ? 'border-red-400' : 'border-slate-200 dark:border-slate-600'
                                        }`}
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('signup.passwordPlaceholder', 'At least 9 characters')}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                                        aria-label={showPassword ? t('common.hidePassword', 'Hide password') : t('common.showPassword', 'Show password')}
                                    >
                                        {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {passwordError ? <p className="text-xs text-red-600">{passwordError}</p> : null}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="inviteCode">
                                    {t('signup.invite.labelShort', 'Class code (optional)')}
                                </Label>
                                <input
                                    id="inviteCode"
                                    value={inviteCode}
                                    onChange={(e) => {
                                        setInviteCode(e.target.value.toUpperCase());
                                        setInvitePreview(null);
                                        if (e.target.value.trim()) setAccountType('student');
                                    }}
                                    onBlur={previewInvite}
                                    className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-950 px-3.5 py-2.5 text-sm uppercase tracking-wider"
                                    placeholder={t('signup.invite.placeholder', 'TCH-XXXXXX')}
                                />
                                {invitePreviewLoading ? (
                                    <p className="text-xs text-slate-500">{t('signup.invite.checking', 'Checking invite…')}</p>
                                ) : null}
                                {invitePreview && !invitePreviewLoading ? (
                                    <div className={`text-xs p-2.5 rounded-lg ${invitePreview.valid ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300'}`}>
                                        {invitePreview.valid
                                            ? t('signup.invite.fromTeacher', 'Invite from {{name}}', { name: invitePreview.teacherName })
                                            : (invitePreview.message || t('signup.invite.invalid', 'Invalid invite code'))}
                                    </div>
                                ) : null}
                            </div>

                            <div className="space-y-3 pt-1">
                                <label className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={acceptedTerms}
                                        onChange={(e) => {
                                            setAcceptedTerms(e.target.checked);
                                            if (e.target.checked) setTermsError('');
                                        }}
                                    />
                                    <span>
                                        {t('signup.termsPrefix', 'I agree to the')}{' '}
                                        <Link to="/terms" className="font-semibold text-indigo-600 dark:text-indigo-400 underline underline-offset-2" target="_blank" rel="noreferrer">
                                            {t('signup.termsLink', 'Terms of Service')}
                                        </Link>
                                        {' '}{t('signup.termsAnd', 'and')}{' '}
                                        <Link to="/privacy" className="font-semibold text-indigo-600 dark:text-indigo-400 underline underline-offset-2" target="_blank" rel="noreferrer">
                                            {t('signup.privacyLink', 'Privacy Policy')}
                                        </Link>
                                        .
                                    </span>
                                </label>
                                {termsError ? <p className="text-xs text-red-600">{termsError}</p> : null}
                                <label className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={marketingEmailConsent}
                                        onChange={(e) => setMarketingEmailConsent(e.target.checked)}
                                    />
                                    <span>
                                        {t('signup.marketingOptIn', 'Optional: send me product updates and learning tips by email. You can unsubscribe anytime.')}
                                    </span>
                                </label>
                            </div>

                            <Button
                                isProcessing={processing}
                                processingSpinner={<AiOutlineLoading className="h-5 w-5 animate-spin" />}
                                className="w-full !rounded-xl py-2.5 text-sm font-semibold text-white enabled:hover:opacity-95"
                                style={{ backgroundColor: authUi.primary }}
                                type="submit"
                            >
                                {t('signup.createAccount', 'Create account')}
                            </Button>

                            <p className="text-center text-sm text-slate-500 dark:text-slate-400 pt-1">
                                {t('signup.alreadyHaveAccount', 'Already have an account?')}{' '}
                                <button
                                    type="button"
                                    onClick={redirectSignIn}
                                    className="font-semibold hover:underline"
                                    style={{ color: authUi.primary }}
                                >
                                    {t('signup.signIn', 'Sign in')}
                                </button>
                            </p>
                        </form>
                    )}
                </AuthCard>
            </AuthShell>
        </Flowbite>
    );
};

export default SignUp;